/**
 * "Report a problem / request a feature" form, opened from the top-bar
 * button, the user menu or Help: the Simplex fields (type, title, details,
 * part of the app, priority, attachments). A screenshot is optional: paste
 * one (Ctrl+V), pick a file, or "Capture this screen" (the browser's own
 * screen picker, one frame, see capture.ts). Files upload through the
 * attachments route after the request is created. The page context (route
 * pattern, module, office, user, build) is attached automatically and never
 * holds patient data.
 */
import {
  FEEDBACK_ATTACHMENTS_PER_REQUEST,
  FEEDBACK_ATTACHMENT_MAX_BYTES,
  FEEDBACK_PRIORITIES,
  FEEDBACK_PRIORITY_LABELS,
  FEEDBACK_TITLE_MAX,
  FEEDBACK_TYPES,
  FEEDBACK_TYPE_HELP,
  FEEDBACK_TYPE_LABELS,
  type FeedbackPriority,
  type FeedbackType,
} from '@velora/contracts/feedback';
import { Button, Dialog, Field, Input, RadioGroup, Select, Textarea, toast } from '@velora/ui';
import { useNavigate } from '@tanstack/react-router';
import { Camera, Paperclip, X } from 'lucide-react';
import { useMemo, useState, type ClipboardEvent, type FormEvent } from 'react';
import { useOfficeScope } from '../../app/shell.js';
import { createRequest, uploadAttachment, useInvalidate } from './api.js';
import { canCaptureScreen, captureScreen, pastedImages } from './capture.js';
import { buildClientContext, formatBytes, type ActiveRouteInfo, type AreaOption } from './logic.js';
import type { FeedbackPrefill } from './store.js';

const GENERAL = 'general';
const ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,.txt,.csv';

export interface RequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  areas: readonly AreaOption[];
  active: ActiveRouteInfo | undefined;
  pathname: string;
  /** Filled in ahead by the caller (Help Center, Ask Velora). */
  prefill?: FeedbackPrefill | null;
}

export function RequestDialog(props: RequestDialogProps) {
  // Remount on open so every request starts from a clean form.
  return props.open ? <RequestForm {...props} /> : null;
}

function RequestForm({ open, onOpenChange, areas, active, pathname, prefill }: RequestDialogProps) {
  const { office, offices } = useOfficeScope();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const activeArea =
    active && areas.some((a) => a.value === active.moduleId) ? active.moduleId : GENERAL;

  const [type, setType] = useState<FeedbackType>(prefill?.type ?? 'bug');
  const [title, setTitle] = useState((prefill?.title ?? '').slice(0, FEEDBACK_TITLE_MAX));
  const [description, setDescription] = useState(prefill?.description ?? '');
  const [area, setArea] = useState<string>(
    prefill?.area && areas.some((a) => a.value === prefill.area) ? prefill.area : activeArea,
  );
  const [priority, setPriority] = useState<FeedbackPriority>('normal');
  const [officeId, setOfficeId] = useState<string>(office?.id ?? offices[0]?.id ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const typeOptions = useMemo(
    () =>
      FEEDBACK_TYPES.map((t) => ({
        value: t,
        label: FEEDBACK_TYPE_LABELS[t],
        description: FEEDBACK_TYPE_HELP[t],
      })),
    [],
  );

  const [capturing, setCapturing] = useState(false);
  const canCapture = useMemo(() => canCaptureScreen(), []);

  function addFiles(list: FileList | readonly File[] | null) {
    if (!list) return;
    setFiles((current) => {
      const next = [...current];
      for (const f of Array.from(list)) {
        if (f.size > FEEDBACK_ATTACHMENT_MAX_BYTES) {
          setError(`${f.name} is larger than ${formatBytes(FEEDBACK_ATTACHMENT_MAX_BYTES)}.`);
          continue;
        }
        if (next.length >= FEEDBACK_ATTACHMENTS_PER_REQUEST) {
          setError(`At most ${FEEDBACK_ATTACHMENTS_PER_REQUEST} files.`);
          break;
        }
        next.push(f);
      }
      return next;
    });
  }

  /** Ctrl+V of an image anywhere in the form attaches it; text pastes as usual. */
  function onPaste(e: ClipboardEvent<HTMLFormElement>) {
    const images = pastedImages(e.clipboardData.items, files.length + 1);
    if (images.length === 0) return;
    e.preventDefault();
    addFiles(images);
  }

  async function capture() {
    setCapturing(true);
    setError(null);
    try {
      // Hide the form while the frame is taken, so it is not in the picture.
      const file = await captureScreen(() => document.body.classList.add('fbk-capturing'));
      if (file) addFiles([file]);
    } catch {
      setError('The screen could not be captured. Paste or add a screenshot instead.');
    } finally {
      document.body.classList.remove('fbk-capturing');
      setCapturing(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError('Add a one-line summary.');
      return;
    }
    if (!officeId) {
      setError('Pick an office.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const viewport =
        typeof window === 'undefined'
          ? null
          : { width: window.innerWidth, height: window.innerHeight };
      const created = await createRequest(officeId, {
        type,
        title: title.trim(),
        description: description.trim() || null,
        area: area === GENERAL ? null : area,
        priority,
        context: buildClientContext(active, pathname, viewport),
      });
      let failed = 0;
      for (const f of files) {
        try {
          await uploadAttachment(officeId, created.id, f);
        } catch {
          failed += 1;
        }
      }
      void invalidate();
      onOpenChange(false);
      toast({
        title: `Request #${created.number} sent`,
        description:
          failed > 0
            ? `${failed} attachment${failed > 1 ? 's' : ''} could not be uploaded.`
            : 'Thank you. Managers can see it under Feature requests.',
        tone: failed > 0 ? 'warning' : 'success',
        action: { label: 'View', onAction: () => void navigate({ to: '/feedback' }) },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The request could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Report a problem or request a feature"
      description="Tell us what happened or what you need. A screenshot helps a lot."
      size="md"
      className="fbk-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="fbk-request-form" loading={busy}>
            Submit request
          </Button>
        </>
      }
    >
      <form
        id="fbk-request-form"
        className="fbk-form"
        onSubmit={(e) => void submit(e)}
        onPaste={onPaste}
      >
        <Field label="Type">
          <RadioGroup
            aria-label="Request type"
            className="fbk-types"
            options={typeOptions}
            value={type}
            onValueChange={(v) => setType(v as FeedbackType)}
          />
        </Field>
        <Field label="Title" required>
          <Input
            value={title}
            maxLength={FEEDBACK_TITLE_MAX}
            placeholder="One-line summary"
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </Field>
        <Field
          label="Details"
          hint="What happened, what you expected, steps to reproduce. Do not include patient names or other patient details."
        >
          <Textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="fbk-form__row">
          <Field label="Which part of the app?">
            <Select
              aria-label="Which part of the app?"
              options={[{ value: GENERAL, label: 'Not sure / general' }, ...areas]}
              value={area}
              onValueChange={setArea}
            />
          </Field>
          <Field label="Priority">
            <Select
              aria-label="Priority"
              options={FEEDBACK_PRIORITIES.map((p) => ({
                value: p,
                label: FEEDBACK_PRIORITY_LABELS[p],
              }))}
              value={priority}
              onValueChange={(v) => setPriority(v as FeedbackPriority)}
            />
          </Field>
        </div>
        {!office && offices.length > 1 ? (
          <Field label="Office">
            <Select
              aria-label="Office"
              options={offices.map((o) => ({ value: o.id, label: o.name }))}
              value={officeId}
              onValueChange={setOfficeId}
            />
          </Field>
        ) : null}
        <Field
          label="Screenshot (optional)"
          hint={`Paste a screenshot with Ctrl+V, capture this screen, or add files (images, PDF or text, up to ${formatBytes(FEEDBACK_ATTACHMENT_MAX_BYTES)} each). Crop out any patient information.`}
        >
          <div className="fbk-files">
            <div className="fbk-files__actions">
              <label className="fbk-files__pick">
                <Paperclip size={14} aria-hidden />
                <span>Add files</span>
                <input
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="fbk-files__input"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
              {canCapture ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  leadingIcon={<Camera size={14} aria-hidden />}
                  loading={capturing}
                  disabled={busy || files.length >= FEEDBACK_ATTACHMENTS_PER_REQUEST}
                  onClick={() => void capture()}
                >
                  Capture this screen
                </Button>
              ) : null}
            </div>
            {files.length > 0 ? (
              <ul className="fbk-files__list">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    <span className="fbk-trunc">{f.name}</span>
                    <span className="fbk-dim">{formatBytes(f.size)}</span>
                    <button
                      type="button"
                      className="fbk-files__remove"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    >
                      <X size={14} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </Field>
        <p className="fbk-context">
          Sent with your request: this page ({active?.route.title ?? 'current page'}), your name,
          the office and the app version. No patient data is attached.
        </p>
        {error ? (
          <p className="fbk-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
